import React, {
    useCallback,
    useEffect,
    useReducer,
    useRef,
    useState
} from 'react';
import {Alert, Spinner} from 'reactstrap';
import {toast} from 'react-toastify';
import {Download} from 'lucide-react';
import {
    threadToMarkdown,
    threadMarkdownFileName,
    saveMarkdown
} from '../threadMarkdown';
import {useAssistantEventHub} from '../hooks/useAssistantEventHub';
import {
    createOptimisticId,
    initialThreadRunState,
    threadRunReducer
} from '../state/threadRunReducer';
import {useGetAssistantInfo, useGetThreadSnapshot} from '../api/threadHooks';
import {useCreateThread} from '../api/threadHooks';
import {useCreateRun} from '../api/runHooks';
import {CreateRunBody, UserMessageItem} from '../api/types';
import {useAssistantContext} from './AssistantContext';
import {StarterPrompt, useAssistantIntegration} from '../AssistantIntegration';
import {PendingPrompt} from '../pendingPrompt';
import {NAVIGATE_TOOL_NAME, parseNavigation} from '../navigation';
import AssistantInfoButton from './AssistantInfoButton';
import ConnectionStatusBar from './ConnectionStatusBar';
import MessageComposer from './MessageComposer';
import MessageList from './MessageList';
import ToolAuthButton from './ToolAuthButton';
import RunStatusBubble from './RunStatusBubble';
import AssistantSwitcher from './AssistantSwitcher';
import UnpublishedBadge from './UnpublishedBadge';
import SysAdminOnlyBadge from './SysAdminOnlyBadge';
import ThreadListDropdown from './ThreadListDropdown';
import {AssistantApiError} from '../api/http';
import {assistantQueryKeys} from '../api/threadHooks';
import {useQueryClient} from '@tanstack/react-query';

const STALE_RUN_TIMEOUT_MS = 20_000;
const STALE_CHECK_INTERVAL_MS = 5_000;

const NO_STARTERS: StarterPrompt[] = [];
const useNoStarterPrompts = (): StarterPrompt[] => NO_STARTERS;

interface AssistantPanelProps {
    assistantKeyOrId: string;
    selectedThreadId?: string;
    onThreadCreated?: (threadId: string) => void;
    onThreadSelect?: (threadId: string) => void;
    onNewThread?: () => void;
    /** When given, the header's name becomes a picker of the assistants the user may open. */
    onSwitchAssistant?: (key: string) => void;
    showHeader?: boolean;
    /** A prompt asked for elsewhere in the app, sent once the assistant can answer. */
    pendingPrompt?: PendingPrompt;
}

/**
 * The chat panel: SignalR per-thread events fold into the reducer; the thread snapshot
 * endpoint provides initial state and reconnect catch-up using the same item shapes.
 */
const AssistantPanel: React.FC<AssistantPanelProps> = ({
    assistantKeyOrId,
    selectedThreadId,
    onThreadCreated,
    onThreadSelect,
    onNewThread,
    onSwitchAssistant,
    showHeader = true,
    pendingPrompt
}) => {
    const [state, dispatch] = useReducer(
        threadRunReducer,
        initialThreadRunState
    );
    const [isApprovalSubmitting, setIsApprovalSubmitting] = useState(false);
    const pendingRunRef = useRef<CreateRunBody | null>(null);

    const {getContext} = useAssistantContext();
    const {onToolCallCompleted, onNavigate, useStarterPrompts} =
        useAssistantIntegration();
    // The host's hook is fixed for the integration's lifetime, so this is always
    // the same hook on every render.
    const useStarters = useStarterPrompts ?? useNoStarterPrompts;
    const starterPrompts = useStarters();
    const assistantInfoQuery = useGetAssistantInfo(assistantKeyOrId);
    const snapshotQuery = useGetThreadSnapshot(selectedThreadId);
    const createThread = useCreateThread();
    const createRun = useCreateRun();
    const queryClient = useQueryClient();

    const refetchSnapshot = snapshotQuery.refetch;

    const catchUp = useCallback(async () => {
        const result = await refetchSnapshot();
        if (result.data) {
            dispatch({
                kind: 'replaceFromServer',
                items: result.data.items,
                activeRun: result.data.activeRun,
                now: Date.now()
            });
        }
    }, [refetchSnapshot]);

    const hub = useAssistantEventHub(
        selectedThreadId,
        event => {
            dispatch({kind: 'event', envelope: event, now: Date.now()});
            // Host refresh seam: a successfully completed tool call may have
            // mutated app data — let the host invalidate matching caches.
            if (event.type === 'item.completed') {
                const data = event.data as
                    | {
                          item?: {
                              kind?: string;
                              toolName?: string;
                              error?: string;
                              output?: string;
                          };
                      }
                    | undefined;
                const item = data?.item;
                if (item?.kind === 'toolCall' && item.toolName && !item.error) {
                    onToolCallCompleted?.(item.toolName);
                    // The navigation tool: the app moves to the page the call
                    // resolved, only on a live completion (never on catch-up).
                    if (item.toolName === NAVIGATE_TOOL_NAME && onNavigate) {
                        const path = parseNavigation(item.output);
                        if (path) {
                            onNavigate(path);
                        }
                    }
                }
            }
        },
        catchUp
    );

    // Initial snapshot load / thread switch
    useEffect(() => {
        dispatch({kind: 'reset'});
    }, [selectedThreadId]);

    // The remembered conversation is gone (deleted elsewhere, or another user's
    // storage): start afresh rather than showing an error forever.
    const snapshotFailed = snapshotQuery.isError;
    useEffect(() => {
        if (snapshotFailed && selectedThreadId) {
            onNewThread?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snapshotFailed, selectedThreadId]);

    useEffect(() => {
        if (snapshotQuery.data) {
            dispatch({
                kind: 'replaceFromServer',
                items: snapshotQuery.data.items,
                activeRun: snapshotQuery.data.activeRun,
                now: Date.now()
            });
        }
    }, [snapshotQuery.data]);

    const startRun = useCallback(
        async (threadId: string, body: CreateRunBody) => {
            try {
                await createRun.mutateAsync({
                    threadId,
                    body,
                    waitUntilJoined: hub.waitUntilJoined
                });
            } catch (error) {
                dispatch({
                    kind: 'event',
                    envelope: {
                        threadId,
                        type: 'run.status',
                        createdAtUtc: new Date().toISOString(),
                        seq: -1,
                        data: {
                            runId: '',
                            status: 'failed',
                            errorMessage:
                                error instanceof Error
                                    ? error.message
                                    : 'Failed to start the run.'
                        }
                    },
                    now: Date.now()
                });
                toast.error(
                    error instanceof Error
                        ? error.message
                        : 'Failed to send the message'
                );
                // An allowance is used up: re-read assistant info so the
                // composer locks with the reason rather than failing again.
                if (
                    error instanceof AssistantApiError &&
                    error.status === 402
                ) {
                    void queryClient.invalidateQueries({
                        queryKey:
                            assistantQueryKeys.assistantInfo(assistantKeyOrId)
                    });
                }
            }
        },
        [createRun, hub.waitUntilJoined, queryClient, assistantKeyOrId]
    );

    // A run queued while the thread was being created fires once the hub has joined
    useEffect(() => {
        if (pendingRunRef.current && selectedThreadId && hub.isJoined) {
            const body = pendingRunRef.current;
            pendingRunRef.current = null;
            void startRun(selectedThreadId, body);
        }
    }, [selectedThreadId, hub.isJoined, startRun]);

    // Staleness watchdog: an active run that has gone quiet gets reconciled from the snapshot
    useEffect(() => {
        if (!state.activeRun || !selectedThreadId) {
            return;
        }

        const interval = window.setInterval(() => {
            if (
                state.lastEventAt > 0 &&
                Date.now() - state.lastEventAt > STALE_RUN_TIMEOUT_MS
            ) {
                void catchUp();
            }
        }, STALE_CHECK_INTERVAL_MS);

        return () => window.clearInterval(interval);
    }, [state.activeRun, state.lastEventAt, selectedThreadId, catchUp]);

    const handleSend = async (text: string, attachmentIds: string[]) => {
        const optimistic: UserMessageItem = {
            id: createOptimisticId(),
            kind: 'userMessage',
            status: 'completed',
            createdAt: new Date().toISOString(),
            text
        };
        dispatch({kind: 'optimisticUserMessage', item: optimistic});

        const body: CreateRunBody = {
            message: {text, attachmentIds},
            pageContext: getContext() ?? undefined
        };

        if (!selectedThreadId) {
            try {
                pendingRunRef.current = body;
                const thread = await createThread.mutateAsync(assistantKeyOrId);
                onThreadCreated?.(thread.id);
            } catch (error) {
                pendingRunRef.current = null;
                dispatch({kind: 'rollbackOptimistic', itemId: optimistic.id});
                toast.error(
                    error instanceof Error
                        ? error.message
                        : 'Failed to start a conversation'
                );
            }
            return;
        }

        await startRun(selectedThreadId, body);
    };

    // "Ask the assistant" elsewhere in the app: send the prompt it left, once
    // the assistant is known to be able to answer (otherwise the composer shows
    // why). Declared after the thread-reset effect, so the fresh conversation
    // is cleared before the message goes in.
    const assistantLoaded = !!assistantInfoQuery.data;
    const canAnswer =
        assistantInfoQuery.data?.providerReady === true &&
        assistantInfoQuery.data?.budget?.level !== 'exhausted';
    useEffect(() => {
        if (!pendingPrompt || !assistantLoaded) {
            return;
        }
        const text = pendingPrompt.take();
        if (text && canAnswer) {
            void handleSend(text, []);
        }
        // handleSend is rebuilt every render; the nonce is what signals a new prompt.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingPrompt?.nonce, assistantLoaded]);

    const handleApprovalDecision = async (
        approvalRequestItemId: string,
        approved: boolean
    ) => {
        if (!selectedThreadId || isApprovalSubmitting) {
            return;
        }

        setIsApprovalSubmitting(true);
        try {
            await startRun(selectedThreadId, {
                approval: {approvalRequestItemId, approved},
                pageContext: getContext() ?? undefined
            });
        } finally {
            setIsApprovalSubmitting(false);
        }
    };

    // Built from the items already on screen, so it needs no request and works offline.
    const handleDownloadTranscript = () => {
        if (state.items.length === 0) {
            return;
        }

        const assistantName = assistantInfo?.name ?? 'Assistant';
        const exportedAt = new Date();

        const markdown = threadToMarkdown({
            assistantName,
            items: state.items,
            exportedAt
        });

        saveMarkdown(
            markdown,
            threadMarkdownFileName(assistantName, exportedAt)
        );
    };

    const handleStop = () => {
        if (selectedThreadId && state.activeRun) {
            void createRun
                .cancelRun(selectedThreadId, state.activeRun.runId)
                .catch(error =>
                    toast.error(
                        error instanceof Error
                            ? error.message
                            : 'Failed to cancel the run'
                    )
                );
        }
    };

    if (assistantInfoQuery.isLoading) {
        return (
            <div className="d-flex justify-content-center p-4">
                <Spinner />
            </div>
        );
    }

    // A 404 here means governance hid the assistant (tenant AI policy, per-customer
    // disable, unpublished). Layout already suppresses the panel + pull-out tab via
    // AssistantPanelContext.isAssistantAvailable; render nothing for standalone hosts too.
    if (assistantInfoQuery.isError || !assistantInfoQuery.data) {
        return null;
    }

    const assistantInfo = assistantInfoQuery.data;
    const effectiveBudget = state.budget ?? assistantInfo.budget ?? null;
    const budgetExhausted = effectiveBudget?.level === 'exhausted';
    const isRunActive = state.activeRun !== null;
    const awaitingApproval = state.activeRun?.phase === 'awaitingApproval';

    // The header wraps when the panel is narrow so the thread controls stay reachable
    // instead of being clipped off the right edge.
    return (
        <div className="d-flex flex-column h-100" style={{minWidth: 0}}>
            {showHeader && (
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2 flex-shrink-0">
                    <div
                        className="d-flex align-items-center gap-2 flex-grow-1"
                        style={{minWidth: 0}}
                    >
                        {onSwitchAssistant ? (
                            <AssistantSwitcher
                                currentKey={assistantInfo.key}
                                currentName={assistantInfo.name}
                                onSelect={onSwitchAssistant}
                            />
                        ) : (
                            <span className="fw-bold">
                                {assistantInfo.name}
                            </span>
                        )}
                        {assistantInfo.sysAdminOnly && <SysAdminOnlyBadge />}
                        {!assistantInfo.isPublished &&
                            !assistantInfo.sysAdminOnly && <UnpublishedBadge />}
                        <AssistantInfoButton
                            assistantInfo={assistantInfo}
                            budget={effectiveBudget}
                            usage={state.usage}
                        />
                    </div>
                    <div className="d-flex align-items-center gap-1 flex-shrink-0 ms-auto">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary d-flex align-items-center"
                            onClick={handleDownloadTranscript}
                            disabled={state.items.length === 0}
                            title={
                                state.items.length === 0
                                    ? 'Nothing to download yet'
                                    : 'Download this conversation as markdown'
                            }
                        >
                            <Download size={14} />
                        </button>
                        <ThreadListDropdown
                            assistantKeyOrId={assistantKeyOrId}
                            selectedThreadId={selectedThreadId}
                            onThreadSelect={threadId =>
                                onThreadSelect?.(threadId)
                            }
                            onNewThread={() => onNewThread?.()}
                        />
                    </div>
                </div>
            )}

            <ConnectionStatusBar
                connectionState={hub.connectionState}
                hasThread={!!selectedThreadId}
            />

            <MessageList
                items={state.items}
                starterPrompts={
                    !selectedThreadId &&
                    !budgetExhausted &&
                    assistantInfo.providerReady
                        ? starterPrompts
                        : undefined
                }
                onStarter={prompt => void handleSend(prompt, [])}
                pendingApprovalItemId={
                    awaitingApproval ? state.pendingApprovalItemId : null
                }
                isApprovalSubmitting={isApprovalSubmitting}
                onApprovalDecision={handleApprovalDecision}
            />

            <RunStatusBubble
                isRunning={isRunActive && !awaitingApproval}
                statusMessage={state.statusMessage}
            />

            {state.error && (
                <Alert color="danger" className="small py-2 my-2 flex-shrink-0">
                    {state.error}
                </Alert>
            )}

            <div className="flex-shrink-0 mt-2">
                <MessageComposer
                    disabled={!assistantInfo.providerReady || budgetExhausted}
                    disabledReason={
                        budgetExhausted
                            ? effectiveBudget?.reason
                            : assistantInfo.providerNotReadyReason
                    }
                    isRunActive={isRunActive && !awaitingApproval}
                    awaitingApproval={awaitingApproval}
                    // Without vision an image is still useful when the
                    // assistant can process it as a file in the code runtime
                    supportsImages={
                        (assistantInfo.model?.supportsVision ?? false) ||
                        (assistantInfo.codeRuntimeAvailable ?? false)
                    }
                    leftAccessory={
                        <ToolAuthButton assistantKeyOrId={assistantKeyOrId} />
                    }
                    onSend={(text, attachmentIds) =>
                        void handleSend(text, attachmentIds)
                    }
                    onStop={handleStop}
                />
            </div>
        </div>
    );
};

export default AssistantPanel;
